import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { applyBranding } from "@/lib/settings-context";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Eye, EyeOff, AlertCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * iter-121.32 — Invite-acceptance hardening.
 *
 *   1.  The public `/auth/invite/:token/resolve` endpoint no longer leaks
 *       the invitee's email — it returns a masked hint (e.g. `j***@e***.com`)
 *       so the user can visually confirm they're on the right page while
 *       an attacker who only has the URL can't learn the email.
 *   2.  Invitee MUST type the full email to accept — backend validates
 *       (case-insensitive). This defends against URL-leak attacks where
 *       a copied/forwarded link reaches someone who isn't the invitee.
 *   3.  Successful acceptance NO LONGER auto-logs-in. The SPA redirects
 *       to /login?email=…&from=invite so the user has to sign in with
 *       the password they just set — making "set password" and "first
 *       sign-in" two distinct verification steps.
 *   4.  Explicit show/hide eye icon on both password fields (Chrome
 *       doesn't render a native reveal button — Edge does, which was
 *       confusing users into thinking the page was broken on Chrome).
 *
 * iter-120 round-4 follow-up #10 — Accept-invite still branches on
 * `sso_required` (resolved from the public resolver) so SSO-invited
 * users see an *activation-only* screen (no password fields — they'll
 * authenticate via the tenant's SSO provider from the login page).
 */
export default function AcceptInvite() {
    const [params] = useSearchParams();
    const nav = useNavigate();
    const token = params.get("token") || "";
    const brand = useBranding();

    const [meta, setMeta] = useState(null);         // resolved invite meta
    const [resolveErr, setResolveErr] = useState(null);
    const [emailConfirm, setEmailConfirm] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [serverError, setServerError] = useState("");
    const [busy, setBusy] = useState(false);
    // iter-121.33 — Same password policy as /signup so the invitee sees
    // the live rules (length + class count) and isn't surprised by a
    // server-side rejection. Fetched alongside the invite resolver.
    const [policy, setPolicy] = useState({ password_min_length: 10, password_required_classes: 3 });

    useEffect(() => {
        if (!token) return;
        Promise.all([
            api.get(`/auth/invite/${encodeURIComponent(token)}/resolve`),
            api.get("/auth/signup/policy").catch(() => ({ data: {} })),
        ])
            .then(([r, p]) => {
                setMeta(r.data);
                // Apply the inviting org's branding (logo / name / theme /
                // colors) so /accept-invite matches the org the invitee is
                // joining — not the platform default.
                if (r.data?.branding) {
                    try { applyBranding(r.data.branding); } catch { /* noop */ }
                }
                setPolicy({
                    password_min_length:        p.data?.password_min_length        || 10,
                    password_required_classes:  p.data?.password_required_classes  || 3,
                });
            })
            .catch((e) => setResolveErr(e?.response?.data?.detail || "Invitation not found or already accepted."));
    }, [token]);

    // Live password-strength meter — mirrors the Signup + ResetPassword UX.
    const pwState = useMemo(() => {
        const lower  = /[a-z]/.test(password);
        const upper  = /[A-Z]/.test(password);
        const digit  = /\d/.test(password);
        const symbol = /[^a-zA-Z0-9]/.test(password);
        const lengthOk  = password.length >= (policy.password_min_length || 10);
        const classesHit = [lower, upper, digit, symbol].filter(Boolean).length;
        const classesOk  = classesHit >= (policy.password_required_classes || 3);
        const matchesConfirm = password.length > 0 && password === confirm;
        return { lengthOk, classesOk, classesHit, matchesConfirm,
                 strongEnough: lengthOk && classesOk && matchesConfirm };
    }, [password, confirm, policy]);

    const submit = async (e) => {
        e.preventDefault();
        if (!meta) return;
        setServerError("");
        if (!meta.sso_required) {
            const emailLc = (emailConfirm || "").trim().toLowerCase();
            if (!emailLc) { setServerError("Please enter the email this invitation was sent to."); return; }
            // iter-121.32 — basic email shape check; backend does the
            // authoritative match against `invitation.email`.
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailLc)) {
                setServerError("Please enter a valid email address."); return;
            }
            // iter-121.33 — Enforce the SAME live policy the backend
            // applies (length + class count + match). Keeps the user from
            // a round-trip "password too weak" rejection.
            if (!pwState.lengthOk) {
                setServerError(`Password must be at least ${policy.password_min_length} characters.`); return;
            }
            if (!pwState.classesOk) {
                setServerError(`Password must use at least ${policy.password_required_classes} of: lowercase, uppercase, digit, symbol.`); return;
            }
            if (!pwState.matchesConfirm) { setServerError("Passwords do not match."); return; }
        }
        setBusy(true);
        try {
            const body = meta.sso_required
                ? { token }
                : { token, password, email: emailConfirm.trim().toLowerCase() };
            const r = await api.post("/auth/accept-invite", body);
            if (r.data?.sso_required) {
                // Provider-aware welcome message, mirroring the SSO branch above.
                const providers = Array.isArray(meta.sso_providers) ? meta.sso_providers : [];
                const pinned = meta.sso_provider
                    || (providers.length === 1 ? providers[0] : null);
                const providerName =
                    pinned === "google" ? "Google" :
                    pinned === "microsoft" ? "Microsoft" :
                    "SSO";
                toast.success(`Account activated — sign in with ${providerName}`);
                nav(`/login?sso_hint=${encodeURIComponent(emailConfirm || "")}`, { replace: true });
                return;
            }
            // iter-121.32 — No auto-login. Backend returns `must_sign_in:
            // true` + the canonical email; SPA redirects to /login with
            // the email pre-filled. The user signs in fresh with the
            // password they just set — providing implicit confirmation
            // that they actually remember it.
            const signInEmail = r.data?.email || emailConfirm.trim().toLowerCase();
            nav(`/login?email=${encodeURIComponent(signInEmail)}&from=invite`, { replace: true });
        } catch (err) {
            const detail = err?.response?.data?.detail || "Failed to accept invitation. The link may have expired — please ask your admin to resend it.";
            setServerError(detail);
        } finally {
            setBusy(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6">
                <div className="text-center">
                    <h1 className="text-2xl font-semibold">Invalid invitation</h1>
                    <p className="text-muted-foreground mt-2">No token provided.</p>
                </div>
            </div>
        );
    }

    if (resolveErr) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6" data-testid="accept-invite-error">
                <div className="text-center max-w-md">
                    <h1 className="text-2xl font-semibold">Invitation unavailable</h1>
                    <p className="text-muted-foreground mt-2 text-sm">{resolveErr}</p>
                    <Button className="mt-6 rounded-sm" onClick={() => nav("/login")} data-testid="accept-invite-go-login">Go to sign in</Button>
                </div>
            </div>
        );
    }

    if (!meta) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6" data-testid="accept-invite-loading">
                <div className="text-center">
                    <div className="w-8 h-8 mx-auto border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <p className="text-muted-foreground mt-3 text-sm">Loading invitation…</p>
                </div>
            </div>
        );
    }

    // Inviting org's branding (from the resolver). Logo-first, and a text
    // wordmark ONLY when the org actually configured a Brand Name — never a
    // hardcoded product name.
    const ib = meta?.branding || {};
    const inviteLogo = ib.logo_url || null;
    const inviteName = (ib.app_name || "").trim();
    const Brand = () => (
        <div className="flex items-center gap-2.5 mb-6">
            {inviteLogo ? (
                <>
                    <img src={inviteLogo} alt={inviteName || "Brand logo"}
                        className="h-10 w-auto max-w-[180px] object-contain" data-testid="invite-brand-logo" />
                    {inviteName && <span className="text-lg font-semibold" data-testid="invite-brand-name">{inviteName}</span>}
                </>
            ) : (
                <>
                    <div className="w-7 h-7 bg-primary flex items-center justify-center" data-testid="invite-brand-mark">
                        <span className="font-mono text-primary-foreground text-sm font-semibold">{(inviteName || brand.appName).charAt(0).toUpperCase()}</span>
                    </div>
                    {inviteName && <span className="text-lg font-semibold" data-testid="invite-brand-name">{inviteName}</span>}
                </>
            )}
        </div>
    );

    // SSO-required activation — no password fields.
    if (meta.sso_required) {
        // iter-120 round-4 follow-up #12 — Provider resolution priority:
        //   1. The per-invite pin (`meta.sso_provider`) — authoritative.
        //   2. Single enabled provider on the tenant (auto-pinned).
        //   3. Multiple providers → generic "single sign-on provider".
        // Fixes the "SSO SSO" bug where the fallback label "SSO" was
        // concatenated with the suffix " SSO" in the JSX.
        const providers = Array.isArray(meta.sso_providers) ? meta.sso_providers : [];
        const pinned = meta.sso_provider
            || (providers.length === 1 ? providers[0] : null);
        const providerName =
            pinned === "google" ? "Google" :
            pinned === "microsoft" ? "Microsoft" :
            null;
        const headerLabel = providerName ? `${providerName} SSO` : "single sign-on";
        const buttonLabel =
            providerName ? `Sign in with ${providerName}` : "Sign in via SSO";
        return (
            <div className="min-h-screen flex items-center justify-center bg-background p-6" data-testid="accept-invite-page">
                <form onSubmit={submit} className="w-full max-w-sm border border-border rounded-sm bg-card p-8">
                    <Brand />
                    <div className="flex items-center gap-2 mb-2">
                        <ShieldCheck className="h-4 w-4 text-primary" />
                        <span className="eyebrow">SSO activation</span>
                    </div>
                    <h1 className="text-2xl font-semibold tracking-tight mb-1" data-testid="accept-invite-title">Activate your account</h1>
                    <p className="text-sm text-muted-foreground mb-4">
                        Hello <b className="font-mono">{meta.email_masked || meta.email || "—"}</b> — your account is configured for <b data-testid="accept-invite-provider-label">{headerLabel}</b>. No password needed; you'll sign in using your work account.
                    </p>
                    <div className="text-[11px] font-mono text-muted-foreground border border-border rounded-sm p-3 mb-4 bg-secondary/30">
                        After activation, click <b data-testid="accept-invite-provider-cta">{buttonLabel}</b> on the login page to access {brand.appName}.
                    </div>
                    <Button type="submit" className="w-full h-10" data-testid="invite-activate-sso" disabled={busy}>
                        {busy ? "Activating…" : "Activate account"}
                    </Button>
                </form>
            </div>
        );
    }

    // Standard password activation.
    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6" data-testid="accept-invite-page">
            <form onSubmit={submit} className="w-full max-w-sm border border-border rounded-sm bg-card p-8">
                <Brand />
                <h1 className="text-2xl font-semibold tracking-tight mb-1" data-testid="accept-invite-title">Accept your invitation</h1>
                <p className="text-sm text-muted-foreground mb-4">
                    Confirm your email and set a password to activate your account.
                </p>

                {/* iter-121.32 — Privacy-preserving email hint + required
                    email-confirmation input. The backend re-validates so
                    the client can't be bypassed by tampering with the
                    request body. */}
                <div className="text-[11px] font-mono text-muted-foreground border border-border rounded-sm p-3 mb-4 bg-secondary/30" data-testid="invite-email-hint">
                    <ShieldCheck className="inline h-3 w-3 -mt-0.5 mr-1 text-primary" />
                    Invitation sent to <b className="text-foreground" data-testid="invite-email-masked">{meta.email_masked || "—"}</b>
                </div>

                <div className="space-y-3">
                    <div>
                        <label className="eyebrow mb-1.5 block">Confirm your email</label>
                        <input
                            type="email"
                            required
                            autoComplete="email"
                            value={emailConfirm}
                            onChange={(e) => setEmailConfirm(e.target.value)}
                            placeholder="you@company.com"
                            className="w-full h-10 px-3 bg-transparent border border-border rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-primary font-mono"
                            data-testid="invite-email-input"
                        />
                    </div>
                    <div>
                        <label className="eyebrow mb-1.5 block">New password</label>
                        <div className="relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="new-password"
                                className="w-full h-10 pl-3 pr-9 bg-transparent border border-border rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-primary font-mono"
                                data-testid="invite-password-input"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword((v) => !v)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                tabIndex={-1}
                                aria-label={showPassword ? "Hide password" : "Show password"}
                                data-testid="invite-password-toggle">
                                {showPassword ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                            </button>
                        </div>
                        {/* iter-121.33 — Live policy checklist mirroring /signup
                            and /reset-password so users have a single, consistent
                            password experience across the platform. */}
                        <div className="text-[10px] text-muted-foreground mt-1.5 space-y-0.5" data-testid="invite-password-rules">
                            <div className={pwState.lengthOk ? "text-[hsl(var(--success))]" : ""}>
                                • {pwState.lengthOk ? "✓" : "○"} At least {policy.password_min_length} characters
                            </div>
                            <div className={pwState.classesOk ? "text-[hsl(var(--success))]" : ""}>
                                • {pwState.classesOk ? "✓" : "○"} At least {policy.password_required_classes} of: lowercase, uppercase, digit, symbol ({pwState.classesHit}/4)
                            </div>
                        </div>
                    </div>
                    <div>
                        <label className="eyebrow mb-1.5 block">Confirm password</label>
                        <div className="relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                required
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                autoComplete="new-password"
                                className="w-full h-10 pl-3 pr-3 bg-transparent border border-border rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-primary font-mono"
                                data-testid="invite-password-confirm"
                            />
                        </div>
                        {confirm.length > 0 && password !== confirm && (
                            <div className="text-[10px] text-destructive mt-1" data-testid="invite-confirm-mismatch">
                                Passwords don't match
                            </div>
                        )}
                    </div>

                    {serverError && (
                        <div className="flex items-start gap-2 text-xs text-destructive border border-destructive/30 bg-destructive/5 rounded-sm p-2.5" data-testid="invite-server-error">
                            <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                            <div>{serverError}</div>
                        </div>
                    )}

                    <Button type="submit" className="w-full h-10" data-testid="invite-accept-submit" disabled={busy || (!meta.sso_required && !pwState.strongEnough) || !emailConfirm.trim()}>
                        {busy ? "Activating…" : "Activate account"}
                    </Button>

                    <div className="text-center pt-1">
                        <p className="text-[11px] text-muted-foreground">
                            After activation, you'll be redirected to sign in with your new password.
                        </p>
                    </div>
                </div>
            </form>
        </div>
    );
}
